import { useCallback, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type AddProductComplianceEvidenceDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  slotTitle: string
  onUploadFiles: (files: File[]) => Promise<void>
}

export function AddProductComplianceEvidenceDialog({
  open,
  onOpenChange,
  slotTitle,
  onUploadFiles,
}: AddProductComplianceEvidenceDialogProps) {
  const { t } = useTranslation('common')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return
      }

      setIsUploading(true)
      try {
        await onUploadFiles(files)
        onOpenChange(false)
      } catch {
        toast.error(t('v2.pages.products.detail.sharedEvidence.actions.uploadFailed'))
      } finally {
        setIsUploading(false)
      }
    },
    [onOpenChange, onUploadFiles, t]
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files
      if (fileList != null && fileList.length > 0) {
        void handleFiles(Array.from(fileList))
      }
      event.target.value = ''
    },
    [handleFiles]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const fileList = event.dataTransfer.files
      if (fileList.length > 0) {
        void handleFiles(Array.from(fileList))
      }
    },
    [handleFiles]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>
            {t('v2.pages.products.detail.sharedEvidence.fileDialog.uploadTitle', {
              slotTitle,
            })}
          </DialogTitle>
          <p className='text-sm text-muted-foreground'>
            {t('v2.pages.products.detail.sharedEvidence.fileDialog.uploadDescription')}
          </p>
        </DialogHeader>
        <div
          className={cn(
            'flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center',
            'border-muted-foreground/25 hover:border-muted-foreground/50',
            isUploading && 'pointer-events-none opacity-60'
          )}
          onDragOver={(event) => {
            event.preventDefault()
          }}
          onDrop={handleDrop}
        >
          <Upload className='mb-2 size-10 text-muted-foreground' />
          <p className='mb-3 text-sm text-muted-foreground'>
            {t('v2.pages.products.detail.sharedEvidence.fileDialog.dragDrop')}
          </p>
          <p className='mb-4 text-xs text-muted-foreground'>
            {t('v2.pages.products.detail.sharedEvidence.fileDialog.orClick')}
          </p>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            className='sr-only'
            onChange={handleInputChange}
            disabled={isUploading}
          />
          <Button
            disabled={isUploading}
            variant='secondary'
            size='sm'
            onClick={() => {
              fileInputRef.current?.click()
            }}
          >
            {t('v2.pages.products.detail.sharedEvidence.fileDialog.selectFile')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
